import React, { useEffect, useState } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "../../ui/dialog";
import { toast } from "sonner";
import { Users, CreditCard, Loader2, RefreshCw, MessageSquarePlus } from "lucide-react";
import { OnboardingPanel } from "../agent-onboarding/OnboardingPanel";
import { ReviewQueue } from "../agent-onboarding/ReviewQueue";

const AGENT_STATUSES = [
  { value: "nieuw", label: "Nieuw", color: "bg-blue-100 text-blue-800" },
  { value: "in_behandeling", label: "In behandeling", color: "bg-amber-100 text-amber-800" },
  { value: "contact_opgenomen", label: "Contact opgenomen", color: "bg-indigo-100 text-indigo-800" },
  { value: "goedgekeurd", label: "Goedgekeurd", color: "bg-green-100 text-green-800" },
  { value: "afgewezen", label: "Afgewezen", color: "bg-red-100 text-red-800" },
  { value: "on_hold", label: "On hold", color: "bg-gray-100 text-gray-700" },
  { value: "aanvullende_informatie_nodig", label: "Aanvullende informatie nodig", color: "bg-amber-100 text-amber-800" },
  { value: "onboarding_gestart", label: "Onboarding gestart", color: "bg-sky-100 text-sky-800" },
  { value: "actief", label: "Actief", color: "bg-green-100 text-green-800" },
];

function StatusBadge({ status }) {
  const meta = AGENT_STATUSES.find((s) => s.value === status) || AGENT_STATUSES[0];
  return (
    <span className={`text-[10px] uppercase tracking-wider font-chivo font-bold px-2 py-0.5 ${meta.color}`}>
      {meta.label}
    </span>
  );
}

function AgentApplicationsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  const [wizard, setWizard] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (search) params.set("q", search);
      const r = await adminApi.get(`/admin/agent-applications?${params.toString()}`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [statusFilter]);

  const updateStatus = async (id, status) => {
    setBusy(true);
    try {
      await adminApi.put(`/admin/agent-applications/${id}/status`, { status });
      toast.success(`Status bijgewerkt naar ${status}`);
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
      if (detail?.id === id) setDetail({ ...detail, status });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!detail || !note.trim()) return;
    setBusy(true);
    try {
      const r = await adminApi.post(`/admin/agent-applications/${detail.id}/notes`, { note });
      setDetail({ ...detail, notes: [...(detail.notes || []), r.data.note] });
      setNote("");
      toast.success("Notitie toegevoegd");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="leads-agent-tab">
      <ReviewQueue onLinked={refresh}/>
      {items.some(it => it.new_information) && <p className="text-sm text-amber-900 bg-amber-50 p-3" data-testid="leads-new-information-notice">Nieuwe informatie ontvangen bij {items.filter(it => it.new_information).length} aanvra(a)g(en).</p>}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          data-testid="leads-agent-search"
          placeholder="Zoek bedrijf, e-mail, stad..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && refresh()}
          className="rounded-none max-w-xs"
        />
        <select
          data-testid="leads-agent-status-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="rounded-none border border-gray-300 bg-white px-3 py-2 text-sm h-10"
        >
          <option value="">Alle statussen</option>
          {AGENT_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <Button onClick={refresh} variant="outline" className="rounded-none" data-testid="leads-agent-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Verversen
        </Button>
        <div className="ml-auto text-xs text-gray-500">{items.length} aanmeldingen</div>
      </div>

      <div className="bg-white border border-gray-200 overflow-hidden">
        <table className="w-full text-sm leads-agent-table">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider font-chivo font-bold text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">Bedrijf</th>
              <th className="px-3 py-2 text-left">Contact</th>
              <th className="px-3 py-2 text-left">Locatie</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Datum</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={6} className="p-6 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Laden...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center text-gray-500">Geen aanmeldingen.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} data-testid={`leads-agent-row-${it.id}`} className="hover:bg-gray-50">
                <td className="px-3 py-2"><div className="font-chivo font-bold text-[#0A192F]">{it.company_name}</div>{it.new_information && <span className="text-xs text-amber-800" data-testid={`leads-new-information-${it.id}`}>Nieuwe informatie</span>}</td>
                <td className="px-3 py-2"><div>{it.first_name} {it.last_name}</div><div className="text-xs text-gray-500">{it.email}</div></td>
                <td className="px-3 py-2"><div>{it.city}, {it.country}</div><div className="text-xs text-gray-500">{it.location_type || "—"}</div></td>
                <td className="px-3 py-2"><StatusBadge status={it.status} /></td>
                <td className="px-3 py-2 text-xs text-gray-500">{new Date(it.created_at).toLocaleString("nl-NL")}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => { setDetail(it); setNote(""); setWizard(false); }} data-testid={`leads-agent-open-${it.id}`}>Open</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="sm:max-w-4xl w-[calc(100%-24px)] max-h-[92vh] overflow-y-auto" data-testid="leads-agent-detail">
          <DialogHeader>
            <DialogTitle className="pr-5 [overflow-wrap:anywhere]">{detail?.company_name}</DialogTitle>
            <DialogDescription>
              Aanmelding ontvangen op {detail?.created_at && new Date(detail.created_at).toLocaleString("nl-NL")}
            </DialogDescription>
          </DialogHeader>
          {detail && <div className="flex gap-2 flex-wrap"><Button variant={wizard ? "outline" : "default"} data-testid="leads-application-details" onClick={()=>setWizard(false)}>Aanvraaggegevens</Button><Button variant={wizard ? "default" : "outline"} data-testid="leads-agent-onboard" onClick={()=>setWizard(true)}>Agent onboarden</Button></div>}
          {detail && wizard && <OnboardingPanel applicationId={detail.id} onChanged={row=>{setDetail(row);setItems(current=>current.map(it=>it.id===row.id?row:it));}}/>}
          {detail && !wizard && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><Label className="text-[10px] uppercase">Contact</Label><div>{detail.first_name} {detail.last_name}</div></div>
                <div><Label className="text-[10px] uppercase">E-mail</Label><div>{detail.email}</div></div>
                <div><Label className="text-[10px] uppercase">Telefoon</Label><div>{detail.phone}</div></div>
                <div><Label className="text-[10px] uppercase">WhatsApp</Label><div>{detail.whatsapp || "—"}</div></div>
                <div><Label className="text-[10px] uppercase">KvK</Label><div>{detail.kvk_number || "—"}</div></div>
                <div><Label className="text-[10px] uppercase">Website</Label><div className="truncate">{detail.website || "—"}</div></div>
                <div><Label className="text-[10px] uppercase">Land/stad</Label><div>{detail.country} / {detail.city}</div></div>
                <div><Label className="text-[10px] uppercase">Type locatie</Label><div>{detail.location_type || "—"}</div></div>
                <div><Label className="text-[10px] uppercase">Opslagruimte</Label><div>{detail.available_storage || "—"}</div></div>
                <div><Label className="text-[10px] uppercase">Openingstijden</Label><div>{detail.opening_hours || "—"}</div></div>
                <div><Label className="text-[10px] uppercase">Personeel</Label><div>{detail.has_staff === true ? "Ja" : detail.has_staff === false ? "Nee" : "—"}</div></div>
                <div><Label className="text-[10px] uppercase">Ervaring</Label><div>{detail.has_logistics_experience === true ? "Ja" : detail.has_logistics_experience === false ? "Nee" : "—"}</div></div>
                <div className="col-span-2"><Label className="text-[10px] uppercase">Korte toelichting</Label><div className="whitespace-pre-wrap">{detail.short_description || "—"}</div></div>
                <div><Label className="text-[10px] uppercase">Gewenste rol</Label><div>{detail.desired_role || "—"}</div></div>
                <div><Label className="text-[10px] uppercase">Bestemmingen</Label><div>{detail.desired_destinations || "—"}</div></div>
                <div className="col-span-2"><Label className="text-[10px] uppercase">Verwacht aantal</Label><div>{detail.expected_shipments || "—"}</div></div>
                <div className="col-span-2"><Label className="text-[10px] uppercase">Opmerking</Label><div className="whitespace-pre-wrap">{detail.note || "—"}</div></div>
              </div>

              <div>
                <Label className="text-[10px] uppercase">Status</Label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {AGENT_STATUSES.filter(s=>!["actief","onboarding_gestart"].includes(s.value)).map((s) => (
                    <Button
                      key={s.value}
                      type="button"
                      size="sm"
                      variant={detail.status === s.value ? "default" : "outline"}
                      className="rounded-none"
                      onClick={() => updateStatus(detail.id, s.value)}
                      disabled={busy || detail.onboarding_version !== 1 || !!detail.activation_operation}
                      data-testid={`leads-agent-status-${s.value}`}
                    >
                      {s.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-[10px] uppercase">Interne notities</Label>
                <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                  {(detail.notes || []).length === 0 ? (
                    <div className="text-xs text-gray-400">Nog geen notities.</div>
                  ) : (detail.notes || []).map((n) => (
                    <div key={n.id} className="bg-gray-50 border-l-2 border-[#0055FF] px-3 py-2 text-xs">
                      <div className="text-gray-500">{n.author_email} · {new Date(n.created_at).toLocaleString("nl-NL")}</div>
                      <div className="text-gray-800 whitespace-pre-wrap mt-1">{n.body}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex gap-2">
                  <Textarea data-testid="leads-agent-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-none" placeholder="Voeg een interne notitie toe..." />
                  <Button onClick={addNote} disabled={busy || !note.trim()} className="rounded-none" data-testid="leads-agent-note-submit">
                    <MessageSquarePlus className="w-4 h-4 mr-1" /> Bewaar
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-none" data-testid="leads-agent-close" onClick={() => setDetail(null)}>Sluiten</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PaymentReferencesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("q", search);
      const r = await adminApi.get(`/admin/payment-references?${params.toString()}`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="space-y-4" data-testid="leads-payments-tab">
      <div className="flex flex-wrap items-center gap-3">
        <Input
          data-testid="leads-payments-search"
          placeholder="Zoek W-nummer, factuur, e-mail..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && refresh()}
          className="rounded-none max-w-xs"
        />
        <Button onClick={refresh} variant="outline" className="rounded-none" data-testid="leads-payments-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Verversen
        </Button>
        <div className="ml-auto text-xs text-gray-500">{items.length} referenties</div>
      </div>

      <div className="bg-white border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase tracking-wider font-chivo font-bold text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">W-nummer</th>
              <th className="px-3 py-2 text-left">Factuur</th>
              <th className="px-3 py-2 text-left">Klant</th>
              <th className="px-3 py-2 text-left">Bedrag</th>
              <th className="px-3 py-2 text-left">Opmerking</th>
              <th className="px-3 py-2 text-left">Datum</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={6} className="p-6 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Laden...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center text-gray-500">Nog geen online betaalreferenties.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} data-testid={`leads-payments-row-${it.id}`} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-mono font-bold text-[#0A192F]">{it.w_number}</td>
                <td className="px-3 py-2">{it.invoice_number || "—"}</td>
                <td className="px-3 py-2"><div>{it.name}</div><div className="text-xs text-gray-500">{it.email}</div></td>
                <td className="px-3 py-2">{it.amount != null ? `€ ${Number(it.amount).toFixed(2)}` : "—"}</td>
                <td className="px-3 py-2 max-w-xs"><div className="truncate text-gray-600">{it.note || "—"}</div></td>
                <td className="px-3 py-2 text-xs text-gray-500">{new Date(it.created_at).toLocaleString("nl-NL")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function LeadsSection() {
  const { hasPermission } = useAuth();
  // Re-use existing perm; section is intended for owner/admin/manager already on backend.
  if (!hasPermission("customers:view")) {
    return (
      <div className="bg-white border border-gray-200 p-10 text-center" data-testid="leads-no-access">
        <h2 className="font-chivo font-black text-xl text-[#0A192F]">Geen toegang</h2>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="leads-section">
      <h1 className="font-chivo font-black text-2xl text-[#0A192F] flex items-center gap-2">
        <Users className="w-6 h-6 text-[#0055FF]" /> Leads &amp; Aanmeldingen
      </h1>
      <Tabs defaultValue="agents">
        <TabsList className="rounded-none grid grid-cols-2 h-auto w-full max-w-full sm:inline-flex sm:w-auto" data-testid="leads-tabs">
          <TabsTrigger value="agents" className="whitespace-normal min-w-0 [overflow-wrap:anywhere]" data-testid="leads-tab-agents"><Users className="w-4 h-4 mr-2 shrink-0" /> Agent-aanmeldingen</TabsTrigger>
          <TabsTrigger value="payments" className="whitespace-normal min-w-0 [overflow-wrap:anywhere]" data-testid="leads-tab-payments"><CreditCard className="w-4 h-4 mr-2 shrink-0" /> Online betaalreferenties</TabsTrigger>
        </TabsList>
        <TabsContent value="agents" className="mt-4"><AgentApplicationsTab /></TabsContent>
        <TabsContent value="payments" className="mt-4"><PaymentReferencesTab /></TabsContent>
      </Tabs>
    </div>
  );
}
